import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  ListChecks, Plus, Trash2, RefreshCw, Check, ArrowRight, Loader,
  CalendarDays, History, Pencil, X, StickyNote,
} from 'lucide-react';

// Daily Tasks — the recurring office round.
//
// Paul sets what a person does and how often; they work it off a dated list and tick it
// as they go. Backed by daily-tasks-sb (settings KV 'assigned_tasks' + 'task_completions').
//
// The three fields that matter on every task are LOOKING / CALL / DONE WHEN. They encode
// the split the whole feature exists for: the engine does the finding, the person does the
// deciding. "The call" is rendered as the most prominent line on the card deliberately —
// it's the part of the task that isn't mechanical.

const URL = '/.netlify/functions/daily-tasks-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json());

const PRIORITY_META = {
  very_high: { label: 'Very High', color: '#ef4444' },
  high: { label: 'High', color: '#f59e0b' },
  normal: { label: 'Normal', color: '#3b82f6' },
};
const PRIORITY_ORDER = ['very_high', 'high', 'normal'];
const SCHEDULE_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PEOPLE_NAMES = { BOUVENGP2: 'Paul', BOUVENGJ: 'Jayda', SOTOCRUZL: 'Luis', SOTOASTOL2: 'Leo', DONGB3: 'Billy' };
const personName = (l) => PEOPLE_NAMES[l] || l;

const labelStyle = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '10px 12px', fontSize: '.85rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const PriorityChip = ({ priority }) => {
  const m = PRIORITY_META[priority] || PRIORITY_META.normal;
  return (
    <span style={{ fontSize: '.66rem', fontFamily: 'var(--font-mono)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: m.color, border: `1px solid ${m.color}`, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
};

// Says WHEN this task is expected, in the words the person actually needs: a daily task
// says nothing useful by saying "daily", but a weekly one that's now two days late does.
const ScheduleChip = ({ task }) => {
  const late = task.overdue && !task.completion?.doneAt;
  const text = task.schedule === 'daily' ? 'Today'
    : task.schedule === 'weekly' ? `Weekly · ${task.dueLabel}`
      : `Monthly · ${task.dueLabel}`;
  return (
    <span style={{ fontSize: '.66rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color: late ? '#f59e0b' : 'var(--hp-dim)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <CalendarDays size={11} /> {text}{late ? ' · due' : ''}
    </span>
  );
};

export default function DailyTasksPage() {
  const { currentUser, addNotification } = useApp();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === 'admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('worklist');
  const [who, setWho] = useState('');            // worklist: whose round to show
  const [chosen, setChosen] = useState(false);   // true once the user picked a person themselves
  const [hist, setHist] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('load');
      if (d.success) setData(d);
      else addNotification(d.error || 'Could not load tasks', 'error');
    } catch { addNotification('Could not load tasks', 'error'); }
    setLoading(false);
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  // Default the worklist to your own round, or — for the owner, who is usually looking
  // at somebody else's — the first person who actually has tasks. Recomputed on every
  // load until the user picks someone themselves: settling on a default while the round
  // was still empty is how you end up staring at "Paul's round, 0 / 0" after assigning
  // eight tasks to Jayda.
  useEffect(() => {
    if (!data || chosen) return;
    const hasTasks = (l) => data.tasks.some((t) => t.assignee === l && t.active !== false);
    if (hasTasks(data.me)) { setWho(data.me); return; }
    const first = data.tasks.find((t) => t.active !== false);
    setWho(first ? first.assignee : data.me);
  }, [data, chosen]);

  const run = async (action, payload, label) => {
    setBusy(label || action);
    try {
      const d = await api(action, payload);
      if (d.success && d.tasks) setData(d);
      else if (!d.success) addNotification(d.error || 'That did not save', 'error');
      return d;
    } catch { addNotification('That did not save', 'error'); return { success: false }; }
    finally { setBusy(''); }
  };

  const loadHistory = useCallback(async () => {
    const d = await api('history', {});
    if (d.success) setHist(d.items);
    else addNotification(d.error || 'Could not load history', 'error');
  }, [addNotification]);

  useEffect(() => { if (tab === 'history' && !hist) loadHistory(); }, [tab, hist, loadHistory]);

  // Every hook runs before the admin gate below — bailing out early would change the
  // hook order between renders and React would tear the component down.
  const assignees = useMemoAssignees(data);

  if (!isAdmin) {
    return (
      <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--hp-dim)' }}>
        Daily Tasks is an admin page.
      </div>
    );
  }

  const isOwner = !!data?.isOwner;
  const people = data?.people || [];

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Daily Tasks</h2>
          <p>The recurring round — what gets done, by whom, and how often</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setHist(null); load(); }} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          ['worklist', 'The round', ListChecks],
          ...(isOwner ? [['assign', 'Set tasks', Pencil]] : []),
          ['history', 'History', History],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(k)}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--hp-dim)' }}>
          <Loader size={16} className="spin" /> Loading…
        </div>
      ) : !data ? null : tab === 'worklist' ? (
        <Worklist
          data={data} who={who || data.me} setWho={(p) => { setChosen(true); setWho(p); }} assignees={assignees}
          isOwner={isOwner} busy={busy} run={run} navigate={navigate}
        />
      ) : tab === 'assign' ? (
        <AssignTab data={data} people={people} busy={busy} run={run} addNotification={addNotification} />
      ) : (
        <HistoryTab items={hist} />
      )}
    </>
  );
}

// Everyone who currently has a task, so the worklist selector never offers an empty round.
function useMemoAssignees(data) {
  return useMemo(() => {
    if (!data) return [];
    const set = new Set(data.tasks.filter((t) => t.active !== false).map((t) => t.assignee));
    if (data.me) set.add(data.me);
    return [...set];
  }, [data]);
}

/* ── The round ─────────────────────────────────────────────────────────────── */

function Worklist({ data, who, setWho, assignees, isOwner, busy, run, navigate }) {
  const mine = data.tasks.filter((t) => t.assignee === who && t.active !== false && t.due);
  const notYet = data.tasks.filter((t) => t.assignee === who && t.active !== false && !t.due);
  const done = mine.filter((t) => t.completion?.doneAt);
  const open = mine.filter((t) => !t.completion?.doneAt);
  const pct = mine.length ? Math.round((done.length / mine.length) * 100) : 0;

  const grouped = PRIORITY_ORDER
    .map((p) => [p, open.filter((t) => t.priority === p)])
    .filter(([, list]) => list.length);

  const today = new Date(`${data.date}T12:00:00Z`);
  const dateLabel = today.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      {/* Summary bar */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: mine.length ? 12 : 0 }}>
          <div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{personName(who)}'s round</div>
            <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>{dateLabel}</div>
          </div>
          {isOwner && assignees.length > 1 && (
            <select className="form-select" value={who} onChange={(e) => setWho(e.target.value)} style={{ width: 150 }}>
              {assignees.map((p) => <option key={p} value={p}>{personName(p)}</option>)}
            </select>
          )}
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', fontWeight: 700, color: pct === 100 ? 'var(--hp-green)' : 'var(--hp-text)' }}>
              {done.length}<span style={{ color: 'var(--hp-dim)', fontSize: '.9rem' }}> / {mine.length}</span>
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.05em' }}>done</div>
          </div>
        </div>
        {mine.length > 0 && (
          <div style={{ height: 5, background: 'var(--hp-surface)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--hp-green)' : 'var(--hp-gold)', transition: 'width .25s ease' }} />
          </div>
        )}
      </div>

      {mine.length === 0 && (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--hp-dim)' }}>
          Nothing due for {personName(who)} today.
          {isOwner && <> Use <strong>Set tasks</strong> to build the round.</>}
        </div>
      )}

      {grouped.map(([priority, list]) => (
        <div key={priority} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
            <PriorityChip priority={priority} />
            <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>
              {list.length} {list.length === 1 ? 'task' : 'tasks'}
            </span>
          </div>
          {list.map((t) => <TaskCard key={t.id} task={t} busy={busy} run={run} navigate={navigate} />)}
        </div>
      ))}

      {done.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 9, fontWeight: 800 }}>
            Done this period
          </div>
          {done.map((t) => <TaskCard key={t.id} task={t} busy={busy} run={run} navigate={navigate} />)}
        </div>
      )}

      {notYet.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 9, fontWeight: 800 }}>
            Later this period
          </div>
          {notYet.map((t) => (
            <div key={t.id} className="card" style={{ padding: '11px 15px', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 11, opacity: 0.62 }}>
              <span style={{ flex: 1, fontSize: '.87rem' }}>{t.title}</span>
              <ScheduleChip task={t} />
              <PriorityChip priority={t.priority} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function TaskCard({ task, busy, run, navigate }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(task.completion?.note || '');
  const [noting, setNoting] = useState(false);
  // A note saved before the tick creates a completion record with no doneAt —
  // 'done' is specifically doneAt, never merely the presence of the record.
  const isDone = !!task.completion?.doneAt;
  const working = busy === `tick-${task.id}`;
  const m = PRIORITY_META[task.priority] || PRIORITY_META.normal;

  const toggle = () => run(isDone ? 'uncompleteTask' : 'completeTask', { id: task.id, note }, `tick-${task.id}`);

  return (
    <div
      className="card"
      style={{
        padding: 0, marginBottom: 9, overflow: 'hidden',
        borderLeft: `3px solid ${isDone ? 'var(--hp-green)' : m.color}`,
        opacity: isDone ? 0.72 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 15px' }}>
        <button
          type="button" onClick={toggle} disabled={working}
          title={isDone ? 'Mark not done' : 'Mark done'}
          style={{
            width: 20, height: 20, flexShrink: 0, marginTop: 2, cursor: 'pointer', padding: 0,
            borderRadius: 5, border: `1.5px solid ${isDone ? 'var(--hp-green)' : 'var(--hp-dim)'}`,
            background: isDone ? 'var(--hp-green)' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
          }}
        >
          {working ? <Loader size={11} /> : isDone ? <Check size={13} /> : null}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            onClick={() => setOpen((o) => !o)}
            style={{ fontSize: '.92rem', fontWeight: 600, cursor: 'pointer', textDecoration: isDone ? 'line-through' : 'none', lineHeight: 1.35 }}
          >
            {task.title}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 5 }}>
            <ScheduleChip task={task} />
            {task.pageLabel && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.68rem', color: 'var(--hp-dim)' }}>{task.pageLabel}</span>
            )}
            {isDone && task.completion.doneAt && (
              <span style={{ fontSize: '.68rem', color: 'var(--hp-green)' }}>
                {personName(task.completion.doneBy)} · {fmtWhen(task.completion.doneAt)}
              </span>
            )}
            {task.completion?.note && (
              <span style={{ fontSize: '.68rem', color: 'var(--hp-dim)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <StickyNote size={11} /> noted
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <PriorityChip priority={task.priority} />
          {task.page && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(task.page)} title={`Open ${task.pageLabel || task.page}`}>
              Open <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div style={{ padding: '0 15px 14px 47px', fontSize: '.85rem' }}>
          {task.looking && <Detail label="What you're looking at" body={task.looking} />}
          {/* The judgement line — deliberately the one that reads loudest. */}
          {task.call && <Detail label="The call" body={task.call} accent />}
          {task.doneWhen && <Detail label="Done when" body={task.doneWhen} green />}
          {task.why && <Detail label="Why" body={task.why} />}

          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Note (what you found, what you decided)</label>
            <div style={{ display: 'flex', gap: 7 }}>
              <input
                className="form-input" value={note} onChange={(e) => { setNote(e.target.value); setNoting(true); }}
                placeholder="Optional — worth writing when the call wasn't obvious"
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-secondary btn-sm"
                disabled={!noting || busy === `note-${task.id}`}
                onClick={async () => { await run('saveNote', { id: task.id, note }, `note-${task.id}`); setNoting(false); }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, body, accent, green }) => (
  <div style={{ marginBottom: 9 }}>
    <div style={{ ...labelStyle, color: accent ? 'var(--hp-gold)' : 'var(--hp-dim)', marginBottom: 2 }}>{label}</div>
    <div style={{ color: accent ? 'var(--hp-text)' : green ? 'var(--hp-green)' : 'var(--hp-dim)', lineHeight: 1.55 }}>{body}</div>
  </div>
);

/* ── Set tasks (owner only) ────────────────────────────────────────────────── */

const emptyDraft = () => ({
  title: '', page: '', pageLabel: '', looking: '', call: '', doneWhen: '', why: '',
  priority: 'normal', schedule: 'daily', weekday: 1, monthDay: 1, assignee: 'BOUVENGJ', active: true,
});

function AssignTab({ data, people, busy, run, addNotification }) {
  const [picked, setPicked] = useState([]);
  const [libAssignee, setLibAssignee] = useState('BOUVENGJ');
  const [draft, setDraft] = useState(null);

  const togglePick = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const addPicked = async () => {
    const d = await run('addFromLibrary', { keys: picked, assignee: libAssignee }, 'lib');
    if (d.success) { setPicked([]); addNotification(`Added to ${personName(libAssignee)}'s round`, 'success'); }
  };

  const save = async () => {
    const d = await run('saveTask', draft, 'save');
    if (d.success) { setDraft(null); addNotification('Task saved', 'success'); }
  };

  return (
    <>
      {/* Library of suggested tasks */}
      {data.library.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: 3 }}>Suggested tasks</div>
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 13 }}>
            Drawn from what the engine is actually carrying. Tick the ones you want run — priority
            and rhythm come pre-set and you can change both after adding.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 13 }}>
            {data.library.map((t) => (
              <label
                key={t.key}
                style={{
                  display: 'flex', gap: 11, alignItems: 'flex-start', padding: '11px 13px', cursor: 'pointer',
                  border: `1px solid ${picked.includes(t.key) ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                  borderRadius: 8, background: picked.includes(t.key) ? 'rgba(201,168,76,0.07)' : 'transparent',
                }}
              >
                <input type="checkbox" checked={picked.includes(t.key)} onChange={() => togglePick(t.key)} style={{ marginTop: 3 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{t.title}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.68rem', color: 'var(--hp-dim)', marginTop: 2 }}>{t.pageLabel}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: '.68rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{SCHEDULE_LABEL[t.schedule]}</span>
                  <PriorityChip priority={t.priority} />
                </div>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>Assign to</span>
            <select className="form-select" value={libAssignee} onChange={(e) => setLibAssignee(e.target.value)} style={{ width: 150 }}>
              {people.map((p) => <option key={p} value={p}>{personName(p)}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" disabled={!picked.length || busy === 'lib'} onClick={addPicked}>
              <Plus size={14} /> Add {picked.length ? `${picked.length} ` : ''}to the round
            </button>
          </div>
        </div>
      )}

      {/* Assigned tasks */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '.95rem', fontWeight: 700 }}>The round</div>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>{data.tasks.length} task{data.tasks.length === 1 ? '' : 's'} assigned</div>
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setDraft(emptyDraft())}>
            <Plus size={14} /> New task
          </button>
        </div>

        {data.tasks.length === 0 ? (
          <div style={{ padding: '18px 0', textAlign: 'center', color: 'var(--hp-dim)', fontSize: '.85rem' }}>
            No tasks yet — add some from the suggestions above, or write your own.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.tasks.map((t) => (
              <AssignedRow key={t.id} task={t} people={people} busy={busy} run={run} onEdit={() => setDraft({ ...t })} />
            ))}
          </div>
        )}
      </div>

      {draft && (
        <TaskEditor
          draft={draft} setDraft={setDraft} people={people}
          onSave={save} onCancel={() => setDraft(null)} busy={busy === 'save'}
        />
      )}
    </>
  );
}

// One assigned task, with the two settings Paul changes most (priority, rhythm) editable
// inline — changing a priority shouldn't mean opening a form.
function AssignedRow({ task, people, busy, run, onEdit }) {
  const patch = (changes) => run('saveTask', { ...task, ...changes }, `row-${task.id}`);
  const working = busy === `row-${task.id}`;

  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
      padding: '11px 13px', border: '1px solid var(--hp-border)', borderRadius: 8,
      opacity: task.active === false ? 0.5 : 1,
    }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{task.title}</div>
        {task.pageLabel && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.67rem', color: 'var(--hp-dim)', marginTop: 2 }}>{task.pageLabel}</div>
        )}
      </div>

      <select className="form-select" value={task.priority} disabled={working}
        onChange={(e) => patch({ priority: e.target.value })} style={{ width: 118 }} title="Priority">
        {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
      </select>

      <select className="form-select" value={task.schedule} disabled={working}
        onChange={(e) => patch({ schedule: e.target.value })} style={{ width: 106 }} title="How often">
        {Object.entries(SCHEDULE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>

      {task.schedule === 'weekly' && (
        <select className="form-select" value={task.weekday} disabled={working}
          onChange={(e) => patch({ weekday: Number(e.target.value) })} style={{ width: 92 }} title="Which day">
          {[1, 2, 3, 4, 5, 6, 0].map((d) => <option key={d} value={d}>{DAY_NAMES[d]}</option>)}
        </select>
      )}
      {task.schedule === 'monthly' && (
        <select className="form-select" value={task.monthDay} disabled={working}
          onChange={(e) => patch({ monthDay: Number(e.target.value) })} style={{ width: 88 }} title="Day of month">
          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      )}

      <select className="form-select" value={task.assignee} disabled={working}
        onChange={(e) => patch({ assignee: e.target.value })} style={{ width: 118 }} title="Who does it">
        {people.map((p) => <option key={p} value={p}>{personName(p)}</option>)}
      </select>

      <button className="btn btn-secondary btn-sm" disabled={working}
        onClick={() => patch({ active: task.active === false })}
        title={task.active === false ? 'Put back in the round' : 'Pause — keeps the task and its history'}>
        {task.active === false ? 'Paused' : 'Active'}
      </button>

      <button className="btn btn-ghost btn-sm" onClick={onEdit} title="Edit the wording"><Pencil size={14} /></button>
      <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} disabled={working}
        onClick={() => { if (window.confirm(`Remove "${task.title}" from the round? Past completions are kept.`)) run('deleteTask', { id: task.id }, `row-${task.id}`); }}
        title="Remove from the round">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function TaskEditor({ draft, setDraft, people, onSave, onCancel, busy }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, zIndex: 60 }}>
      <div className="card" style={{ width: 640, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
          <div style={{ fontSize: '1rem', fontWeight: 700 }}>{draft.id ? 'Edit task' : 'New task'}</div>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={onCancel}><X size={16} /></button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Task</label>
          <input className="form-input" style={{ width: '100%' }} value={draft.title}
            onChange={(e) => set('title', e.target.value)} placeholder="What she does" />
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 190px' }}>
            <label style={labelStyle}>Page it lives on</label>
            <input className="form-input" style={{ width: '100%' }} value={draft.page}
              onChange={(e) => set('page', e.target.value)} placeholder="/shift-approvals" />
          </div>
          <div style={{ flex: '1 1 190px' }}>
            <label style={labelStyle}>Where exactly</label>
            <input className="form-input" style={{ width: '100%' }} value={draft.pageLabel}
              onChange={(e) => set('pageLabel', e.target.value)} placeholder="Shift Approvals › Pending" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 130px' }}>
            <label style={labelStyle}>Priority</label>
            <select className="form-select" style={{ width: '100%' }} value={draft.priority} onChange={(e) => set('priority', e.target.value)}>
              {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 130px' }}>
            <label style={labelStyle}>How often</label>
            <select className="form-select" style={{ width: '100%' }} value={draft.schedule} onChange={(e) => set('schedule', e.target.value)}>
              {Object.entries(SCHEDULE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {draft.schedule === 'weekly' && (
            <div style={{ flex: '1 1 110px' }}>
              <label style={labelStyle}>Which day</label>
              <select className="form-select" style={{ width: '100%' }} value={draft.weekday} onChange={(e) => set('weekday', Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6, 0].map((d) => <option key={d} value={d}>{DAY_NAMES[d]}</option>)}
              </select>
            </div>
          )}
          {draft.schedule === 'monthly' && (
            <div style={{ flex: '1 1 110px' }}>
              <label style={labelStyle}>Day of month</label>
              <select className="form-select" style={{ width: '100%' }} value={draft.monthDay} onChange={(e) => set('monthDay', Number(e.target.value))}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          <div style={{ flex: '1 1 130px' }}>
            <label style={labelStyle}>Who does it</label>
            <select className="form-select" style={{ width: '100%' }} value={draft.assignee} onChange={(e) => set('assignee', e.target.value)}>
              {people.map((p) => <option key={p} value={p}>{personName(p)}</option>)}
            </select>
          </div>
        </div>

        {[
          ['looking', "What she's looking at", 'The screen the engine puts in front of her'],
          ['call', 'The call she makes', 'The judgement that is actually hers — the part the engine will not guess'],
          ['doneWhen', 'Done when', 'How she knows to tick it'],
          ['why', 'Why it matters', 'Optional'],
        ].map(([k, label, hint]) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <label style={labelStyle}>{label}</label>
            <textarea
              className="form-input" rows={k === 'call' ? 3 : 2} style={{ width: '100%', resize: 'vertical' }}
              value={draft[k]} onChange={(e) => set(k, e.target.value)} placeholder={hint}
            />
          </div>
        ))}

        <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end', marginTop: 4 }}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !draft.title.trim()} onClick={onSave}>
            {busy ? 'Saving…' : 'Save task'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── History ───────────────────────────────────────────────────────────────── */

function HistoryTab({ items }) {
  if (!items) {
    return <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={16} /> Loading…</div>;
  }
  if (!items.length) {
    return <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--hp-dim)' }}>Nothing ticked off yet.</div>;
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr>
              <th style={th}>Done</th>
              <th style={th}>Task</th>
              <th style={th}>Period</th>
              <th style={th}>By</th>
              <th style={th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={`${i.taskId}|${i.period}`}>
                <td style={{ ...td, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '.75rem' }}>{fmtWhen(i.doneAt)}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{i.title}</span>
                    <PriorityChip priority={i.priority} />
                  </div>
                </td>
                <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: '.75rem', color: 'var(--hp-dim)' }}>{i.period}</td>
                <td style={td}>{personName(i.doneBy)}</td>
                <td style={{ ...td, color: 'var(--hp-dim)' }}>{i.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
